/**
 * ALL authorization rules live here (and in matrix.ts) so they can be reviewed in one place.
 * Since Step 15 a role is a matrix of module × action with a data scope per module; a person
 * has a built-in role or a custom one (a copy of a built-in role, changed).
 */
import {
  BUILTIN_MATRICES,
  BUILTIN_ROLES,
  matrixAllows,
  matrixWithin,
  permissionParts,
  SESSION_ONLY,
  type BuiltinRole,
  type Matrix,
  type Permission,
} from './matrix';

export * from './matrix';
export * from './access';

/** What is stored on a membership / invite / API key: a built-in role, or "custom" + its id. */
export const ROLES = [...BUILTIN_ROLES, 'custom'] as const;
export type Role = (typeof ROLES)[number];

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

export const isBuiltinRole = (value: string): value is BuiltinRole =>
  (BUILTIN_ROLES as readonly string[]).includes(value);

/** How the caller authenticated. */
export type AuthKind = 'session' | 'apiKey';

/** The rights of a role: a built-in role's matrix, or the custom role's own. */
export function matrixOf(role: Role, customMatrix?: Matrix | null): Matrix {
  return role === 'custom' ? (customMatrix ?? {}) : BUILTIN_MATRICES[role];
}

export function can(actor: { matrix: Matrix; kind: AuthKind }, permission: Permission): boolean {
  if (SESSION_ONLY.has(permission) && actor.kind !== 'session') return false;
  const [module, action] = permissionParts(permission);
  return matrixAllows(actor.matrix, module, action);
}

interface RoleHolder {
  role: Role;
  matrix: Matrix;
}

/**
 * May `actor` give someone the role `target` (invite, role change)? Owners may give any role.
 * Everyone else needs "Users: edit", can never give Owner, and only a role whose rights are
 * all within their own (no escalation).
 */
export function canAssignRole(actor: RoleHolder, target: RoleHolder): boolean {
  if (actor.role === 'owner') return true;
  if (target.role === 'owner') return false;
  return matrixAllows(actor.matrix, 'users', 'edit') && matrixWithin(target.matrix, actor.matrix);
}

/**
 * May `actor` change, deactivate or remove a member who has `target`? Only owners touch owners;
 * others need "Users: edit" and may only manage people whose rights are within their own.
 * (The "last owner" rule is enforced separately, with a row lock, in the team service.)
 */
export function canManageMember(actor: RoleHolder, target: RoleHolder): boolean {
  if (actor.role === 'owner') return true;
  if (target.role === 'owner') return false;
  return matrixAllows(actor.matrix, 'users', 'edit') && matrixWithin(target.matrix, actor.matrix);
}

/** An API key never has the Owner role, and never more rights than the person creating it. */
export function canCreateApiKeyWithRole(actor: RoleHolder, key: RoleHolder): boolean {
  return key.role !== 'owner' && matrixWithin(key.matrix, actor.matrix);
}

/** Roles a custom role may be copied from (Owner never). */
export const COPYABLE_ROLES: readonly BuiltinRole[] = BUILTIN_ROLES.filter((r) => r !== 'owner');
